"use client";

import type { RoiProjection } from "@/lib/roi-calc/engine";
import type { FunnelPlannerContent } from "@/lib/content/entries/funnel-planner";
import type { AppLocale } from "@/lib/funnel-planner/schema";
import { formatFunnelCurrency, funnelText } from "@/lib/funnel-planner/i18n";

export function ProjectionChart({
  projection,
  content,
  locale,
}: {
  projection: RoiProjection;
  content: FunnelPlannerContent;
  locale: AppLocale;
}) {
  const points = projection.points;
  if (!points || points.length === 0) return null;

  const width = 560;
  const height = 200;
  const padX = 40;
  const padY = 16;
  const chartW = width - padX * 2;
  const chartH = height - padY * 2;

  const revenues = points.map((p) => p.revenue);
  const minRev = Math.min(...revenues);
  const maxRev = Math.max(...revenues);
  const xDenominator = Math.max(1, points.length - 1);
  const yRange = Math.max(1, maxRev - minRev);

  const toX = (i: number) => padX + (i / xDenominator) * chartW;
  const toY = (v: number) => padY + chartH - ((v - minRev) / yRange) * chartH;

  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${toX(i).toFixed(1)},${toY(p.revenue).toFixed(1)}`)
    .join(" ");

  const baseline = projection.baselineRevenue;
  const baseY = toY(baseline).toFixed(1);

  return (
    <div className="fp-pro-chart-scroll w-full overflow-x-auto">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full"
        role="img"
        data-projection-horizon-months={projection.projectionMonths}
        aria-label={funnelText(content, "projectionChartAria", "Monthly revenue projection over {months} months", { months: projection.projectionMonths })}
      >
        {/* Baseline dashed line */}
        <line
          x1={padX}
          y1={baseY}
          x2={width - padX}
          y2={baseY}
          stroke="currentColor"
          strokeDasharray="4 3"
          strokeWidth={1}
          className="text-muted-foreground/40"
        />

        {/* Projection line */}
        <path d={linePath} fill="none" stroke="currentColor" strokeWidth={2} className="text-primary" />

        {/* Month labels */}
        {(() => {
          const horizon = projection.projectionMonths;
          const interval = Math.max(1, Math.ceil(horizon / 4));
          const tickMonths = new Set<number>([0, horizon]);
          for (let month = interval; month < horizon; month += interval) {
            tickMonths.add(month);
          }
          return [...tickMonths].sort((left, right) => left - right).map((month) => {
            const pointIndex = points.findIndex((point) => point.month === month);
            if (pointIndex === -1) return null;
            return (
              <text
                key={month}
                x={toX(pointIndex)}
                y={height - 2}
                textAnchor="middle"
                fontSize={10}
                fill="currentColor"
                className="text-muted-foreground"
              >
                {funnelText(content, "projectionChartMonthLabel", "M{month}", { month })}
              </text>
            );
          });
        })()}
        {/* End label */}
        {points.length > 0 && (
          <text
            x={toX(points.length - 1)}
            y={toY(revenues[revenues.length - 1] ?? 0) - 6}
            textAnchor="end"
            fontSize={10}
            fill="currentColor"
            className="text-primary font-medium"
          >
            {formatFunnelCurrency(revenues[revenues.length - 1] ?? 0, locale)}
          </text>
        )}
      </svg>
    </div>
  );
}
